import { Badge, Flex, Text, Section, Switch, TextField } from "@radix-ui/themes";
import { useCallback, useMemo, useState } from "react";
import { useGetSkills } from "@/v2/entities/skill/useGetSkills";
import {
  buildSkillSlug,
  getBrowserSkillConfig,
  isBrowserSkill,
  Skill,
} from "@/v2/entities/skill/Skill.model";
import { toast } from "sonner";
import { Pencil1Icon, CheckIcon, TrashIcon, PlusIcon, MagnifyingGlassIcon } from "@radix-ui/react-icons";
import {
  CreateBrowserSkillPayload,
  UpdateSkillPayload,
  useSkillApi,
} from "@/v2/entities/skill/Skill.api";
import { useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";
import { PageLayoutV2 } from "@/v2/components/layout/page-layout/PageLayout";
import { PageHeader } from "@/v2/components/layout/page-header/PageHeader";
import AiProgramming from "@/v2/assets/icons/AiProgramming";
import { PageColors, ENavigate } from "@/components/sidebar/sidebar.types";
import { RowActions, SimpleTable } from "@/v2/components/table/SimpleTable";
import { ColumnDef } from "@tanstack/react-table";
import { TextWithTooltip } from "@/components/shared/tooltip";
import { CreateSkill } from "./CreateSkill";
import { EditSkill } from "./EditSkill";
import { skillTemplateSlugs } from "./templates";
import { BrowserSkillModal, BrowserSkillFormValues } from "./BrowserSkillModal";

type SkillRow = Skill & {
  hasCurl: boolean;
  isTemplate: boolean;
  isBrowser: boolean;
  startUrl: string;
  requiresAuthentication: boolean;
  profileName?: string | null;
};

export function Skills() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { deleteSkill, createBrowserSkill, updateSkill } = useSkillApi();
  const { data, refetch } = useGetSkills();

  const browserSkillEnabled = useFlag("browserSkill", false);
  const hitlApprovalEnabled = useFlag("hitlApprovalRequests", false);

  const [skillToEdit, setSkillToEdit] = useState<Skill | undefined>();
  const [browserModalOpen, setBrowserModalOpen] = useState(false);
  const [searchPhrase, setSearchPhrase] = useState("");

  const { mutateAsync: createBrowserSkillMutation, isLoading: isCreatingBrowser } =
    useMutation<
      Skill,
      unknown,
      CreateBrowserSkillPayload
    >({
      mutationKey: ["createBrowserSkill"],
      mutationFn: createBrowserSkill,
      onError: () => {
        toast.error("Failed to create browser skill");
      },
      onSuccess: () => {
        toast("Browser skill created");
      },
      onSettled: () => {
        queryClient.invalidateQueries(["skills"]);
      },
    });


  const { mutate: deleteSkillMutation } = useMutation<
    Skill,
    unknown,
    string,
    { previousSkills?: Skill[] }
  >({
    mutationKey: ["deleteSkill"],
    mutationFn: deleteSkill,
    onMutate: async (skillId) => {
      await queryClient.cancelQueries(["skills"]);
      const previousSkills = queryClient.getQueryData<Skill[]>(["skills"]);
      queryClient.setQueryData<Skill[]>(["skills"], (old = []) =>
        old.filter((skill) => skill.id !== skillId)
      );
      return { previousSkills };
    },
    onError: (_error, _skillId, context) => {
      if (context?.previousSkills) {
        queryClient.setQueryData(["skills"], context.previousSkills);
      }
      toast.error("Failed to delete skill");
    },
    onSuccess: () => {
      toast("Skill deleted successfully");
    },
    onSettled: () => {
      queryClient.invalidateQueries(["skills"]);
    },
  });

  const { mutate: updateSkillMutation } = useMutation<
    Skill,
    unknown,
    UpdateSkillPayload,
    { previousSkills?: Skill[] }
  >({
    mutationKey: ["updateSkill"],
    mutationFn: updateSkill,
    onMutate: async (payload) => {
      await queryClient.cancelQueries(["skills"]);
      const previousSkills = queryClient.getQueryData<Skill[]>(["skills"]);
      queryClient.setQueryData<Skill[]>(["skills"], (old = []) =>
        old.map((skill) =>
          skill.id === payload.id
            ? { ...skill, ...payload }
            : skill
        )
      );
      return { previousSkills };
    },
    onError: (_error, _payload, context) => {
      if (context?.previousSkills) {
        queryClient.setQueryData(["skills"], context.previousSkills);
      }
      toast.error("Failed to update skill");
    },
    onSettled: () => {
      queryClient.invalidateQueries(["skills"]);
    },
  });

  const onToggleApproval = useCallback(
    (skill: SkillRow) => {
      updateSkillMutation({
        id: skill.id,
        requiresApproval: !skill.requiresApproval,
      });
    },
    [updateSkillMutation]
  );

  const columns = useMemo<ColumnDef<SkillRow>[]>(
    () => [
      {
        header: "Skill",
        accessorKey: "name",
        cell: ({ row }) => {
          return (
            <Flex align={"center"} gap={"2"}>
              <Flex
                align="center"
                justify="center"
                style={{
                  width: "20px",
                  height: "20px",
                  backgroundColor: "var(--orange-3)",
                  borderRadius: "4px",
                }}
              >
                <AiProgramming size={16} fill="orange" />
              </Flex>
              <Text size={"2"} color="gray" weight="medium" highContrast>
                {row.original.name}
              </Text>
            </Flex>
          );
        },
      },
      {
        header: "Description",
        accessorKey: "description",
        cell: ({ row }) => {
          return (
            <Flex overflow="hidden">
              <TextWithTooltip size="1" color="gray" truncate>
                {row.original.description}
              </TextWithTooltip>
            </Flex>
          );
        },
      },
      {
        header: "Slug",
        accessorKey: "slug",
        cell: ({ row }) => {
          return (
            <Flex gap="1" align="center" wrap="wrap" overflow="hidden">
              <Badge size={"2"} color="gray" variant="soft">
                {row.original.slug}
              </Badge>

              {row.original.isTemplate && (
                <Badge size={"2"} color="blue" variant="soft">
                  Built-in
                </Badge>
              )}
            </Flex>
          );
        },
        meta: {
          className: "w-1/4",
        },
      },
      {
        header: "Type",
        accessorKey: "type",
        cell: ({ row }) => {
          const { isBrowser, startUrl, requiresAuthentication } = row.original;
          return (
            <Flex direction="column" gap="1" align="start">
              <Flex gap="2" align="center">
                <Badge
                  size="2"
                  color={isBrowser ? "orange" : "gray"}
                  variant="soft"
                >
                  {isBrowser ? "Browser" : "API"}
                </Badge>
                {requiresAuthentication && isBrowser && (
                  <Badge size="2" color="amber" variant="soft">
                    Auth
                  </Badge>
                )}
              </Flex>
              {isBrowser && (
                <TextWithTooltip size="1" color="gray" truncate>
                  {startUrl || "—"}
                </TextWithTooltip>
              )}
            </Flex>
          );
        },
        meta: {
          className: "w-1/4",
        },
      },
      ...(hitlApprovalEnabled
        ? [
            {
              header: "Requires Approval",
              accessorKey: "requiresApproval",
              cell: ({ row }: { row: { original: SkillRow } }) => {
                return (
                  <Switch
                    size="1"
                    checked={row.original.requiresApproval}
                    onCheckedChange={() => onToggleApproval(row.original)}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  />
                );
              },
              meta: {
                className: "w-[140px]",
              },
            } satisfies ColumnDef<SkillRow>,
          ]
        : []),
      {
        header: "cURL",
        accessorKey: "hasCurl",
        cell: ({ row }) => {
          if (row.original.isBrowser) {
            return (
              <Text size="2" color="gray">
                —
              </Text>
            );
          }

          if (row.original.hasCurl) {
            return <CheckIcon width={"20"} height={"20"} />;
          }

          return (
            <Text size="2" color="gray">
              —
            </Text>
          );
        },
        meta: {
          className: "w-[80px]",
        },
      },
    ],
    [onToggleApproval, hitlApprovalEnabled]
  );

  const onDeleteClick = useCallback(
    (skill: SkillRow) => {
      deleteSkillMutation(skill.id);
    },
    [deleteSkillMutation]
  );

  const onEditClick = useCallback(
    (skill: SkillRow) => {

      if (skill.isTemplate && !skill.slug.startsWith("snowflake_")) {
        return;
      }

      if (skill.isBrowser) {
          navigate(`/skills/${skill.id}/browser`);
          return;
      }

      if (!skill.hasCurl) {
          return;
      }

      if (skill.slug.startsWith("snowflake_")) {
        navigate(`/automation-center/skills/${skill.id}/edit/snowflake`);
      } else {
        navigate(`/automation-center/skills/${skill.id}/edit`);
      }
    },
    [navigate]
  );

  const onRowClick = useCallback(
    (skill: SkillRow) => {
      if (skill.isBrowser) {
        navigate(`/skills/${skill.id}/browser`);
        return;
      }

      if (skill.isTemplate || !skill.hasCurl) {
        return;
      }

      if (skill.slug.startsWith("snowflake_")) {
        navigate(`/automation-center/skills/${skill.id}/edit/snowflake`);
      } else {
        navigate(`/automation-center/skills/${skill.id}/edit`);
      }
    },
    [navigate]
  );

  const onDiscardEdit = useCallback(() => {
    setSkillToEdit(undefined);
  }, [setSkillToEdit]);

  const onSuccessEdit = useCallback(() => {
    setSkillToEdit(undefined);
    refetch();
  }, [setSkillToEdit, refetch]);

  const handleOpenBrowserModal = useCallback(() => {
    setBrowserModalOpen(true);
  }, []);

  const handleBrowserModalChange = useCallback((openState: boolean) => {
    setBrowserModalOpen(openState);
  }, []);


  const handleBrowserSkillSubmit = useCallback(
    async (values: BrowserSkillFormValues) => {
      try {
        const createdSkill = await createBrowserSkillMutation({
          name: values.name,
          description: values.description,
          slug: values.slug ?? buildSkillSlug(values.name),
          startUrl: values.startUrl,
        });

        if (createdSkill) {
          setBrowserModalOpen(false);
          navigate(`/skills/${createdSkill.id}/browser`);
        }
      } catch {
        return;
      }
    },
    [
      createBrowserSkillMutation,
      buildSkillSlug,
      navigate,
    ]
  );

  const getRowActions = useCallback(
    ({ isTemplate, slug }: SkillRow): RowActions<SkillRow> => {
      if (isTemplate && !slug.startsWith("snowflake_")) {
        return [
          {
            label: "Delete",
            icon: <TrashIcon />,
            onClick: onDeleteClick,
          },
        ];
      }

      return [
        {
          label: "Edit",
          icon: <Pencil1Icon />,
          onClick: onEditClick,
        },
        {
          label: "Delete",
          icon: <TrashIcon />,
          onClick: onDeleteClick,
        },
      ];
    },
    [onEditClick, onDeleteClick]
  );

  const filteredData = useMemo(() => {
    const search = searchPhrase.toLowerCase();
    return data?.filter((skill) => {
      const browserConfig = getBrowserSkillConfig(skill);
      return (
        skill.name.toLowerCase().includes(search) ||
        skill.description.toLowerCase().includes(search) ||
        browserConfig.startUrl.toLowerCase().includes(search)
      );
    });
  }, [data, searchPhrase]);

  const rows = useMemo(() => {
    return (
      filteredData?.map((skill) => {
        const browserConfig = getBrowserSkillConfig(skill);
        const isBrowser = isBrowserSkill(skill);

        return {
          ...skill,
          isTemplate: skillTemplateSlugs.includes(skill.slug),
          hasCurl:
            !skillTemplateSlugs.includes(skill.slug) &&
            (skill.curl ?? "").trim() !== "",
          isBrowser,
          startUrl: browserConfig.startUrl,
          requiresAuthentication: browserConfig.requiresAuthentication,
          profileName: browserConfig.profileName ?? null,
        };
      }) ?? []
    );
  }, [filteredData]);

  return (
    <>
      <PageLayoutV2>
        <Flex direction="column">
          <PageHeader
            title="Skills"
            description="Create and manage skills that extend your AI agent's capabilities"
            icon={<AiProgramming size={24} fill={PageColors[ENavigate.SKILLS] || "gray"} />}
            actions={
              <Flex gap="2" align="center">
                <TextField.Root
                  placeholder="Search skills"
                  size="2"
                  value={searchPhrase}
                  onChange={(e) => setSearchPhrase(e.target.value)}
                >
                  <TextField.Slot>
                    <MagnifyingGlassIcon height="16" width="16" />
                  </TextField.Slot>
                </TextField.Root>
                <CreateSkill
                  existingSkills={data}
                  onSuccess={refetch}
                  onBrowserSkill={
                    browserSkillEnabled ? handleOpenBrowserModal : undefined
                  }
                />
              </Flex>
            }
          />

          <Section size="4" pt="0">
            <Flex p="6" direction="column" gap="3" width="100%">
              <Flex direction={"row"} gap={"2"} align="center">
                <Flex gap={"2"} align={"center"}>
                  <Text size={"3"} weight={"medium"}>
                    Skills
                  </Text>
                  <Badge color="gray">{data?.length ?? 0}</Badge>
                </Flex>
              </Flex>
              <SimpleTable
                data={rows}
                columns={columns}
                getRowActions={getRowActions}
                onRowClicked={onRowClick}
              />
            </Flex>
          </Section>
        </Flex>
      </PageLayoutV2>

      <BrowserSkillModal
        open={browserModalOpen}
        onOpenChange={handleBrowserModalChange}
        mode="create"
        existingSkills={data ?? []}
        loading={isCreatingBrowser}
        onSubmit={handleBrowserSkillSubmit}
      />

      {skillToEdit && (
        <EditSkill
          skill={skillToEdit}
          refetch={refetch}
          onSuccess={onSuccessEdit}
          onDiscard={onDiscardEdit}
        />
      )}
    </>
  );
}
